<script setup lang="ts">
import { SubscriptionType } from "~/composables/useAuthentication";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { Chapter } from "~/composables/useCourse";
import { CourseChapterType, CourseContentActivityType, CourseType } from "~/enums/course.enums";

const{ user } = useUserStore();

const { course } = useCourseState();

const props = withDefaults(defineProps<{
  chapters: Chapter[];
  activeIndex: number | null;
  allChaptersDefaultExpanded?: boolean;
}>(), {
  allChaptersDefaultExpanded: false,
});

const showFreeChapterBadge = (chapter: Chapter) => {
  return user.value?.subscription.type === SubscriptionType.trial && (
    course.value?.isFreeCmeCourse ||
    chapter.contents.every(activity => !activity.locked || !activity.access)
  );
};

const kickoffMasteryCourseChapterLockedTooltip = (chapter: Chapter): string | null => {
  if (!course.value?.kickoffAndMasteryQuizzesEnabled) {
    return null;
  }

  if (chapter?.type === CourseChapterType.Mastery) {
    return 'The Mastery quiz is not available yet. You need to complete the Kick-off quiz and all the chapters to unlock it.';
  } else {
    return 'The chapter is not available yet. You need to complete the Kick-off quiz first in order to unlock it.';
  }
};

const chapterProgress = (chapter: Chapter) => {
  const totalActivities = chapter.contents
    .filter(content => ![CourseContentActivityType.Comparison, CourseContentActivityType.Summary].includes(content.type))
    .length;

  const completedActivities = chapter.contents.filter(activity => {
    if (activity.type === CourseContentActivityType.Lesson) {
      return activity.completed;
    }

    if ([CourseContentActivityType.Quiz, CourseContentActivityType.KickoffQuiz, CourseContentActivityType.MasteryQuiz].includes(activity.type)) {
      return activity.progress?.finished && activity.progress?.passed;
    }

    return false;
  });

  const isComplete = completedActivities.length === totalActivities;

  return {
    completedActivities,
    totalActivities,
    isComplete,
  };
};

const chapterContents = (chapter: Chapter) => {
  const formatCount = (count: number, singular: string, plural: string) => {
    return `${count} ${count > 1 ? plural : singular}`;
  };

  const lessonsCount = chapter.contents
    .filter(activity => activity.type === CourseContentActivityType.Lesson)
    .length;

  const quizzesCount = chapter.contents
    .filter(activity => [CourseContentActivityType.Quiz, CourseContentActivityType.KickoffQuiz, CourseContentActivityType.MasteryQuiz].includes(activity.type))
    .length;

  if (!lessonsCount && !quizzesCount) {
    return '';
  }

  const resultParts: string[] = [];

  if (lessonsCount) {
    resultParts.push(formatCount(lessonsCount, 'lesson', 'lessons'));
  }

  if (quizzesCount) {
    resultParts.push(formatCount(quizzesCount, 'quiz', 'quizzes'));
  }

  return resultParts.join(', ');
};

const showChapterContentsAndTotalDuration = (chapter: Chapter) => {
  return course.value?.type !== CourseType.Workshop && ![CourseChapterType.Kickoff, CourseChapterType.Mastery].includes(chapter?.type as CourseChapterType);
};

const currentActiveIndex = ref<number[]>([]);

onBeforeMount(() => {
  if (props.allChaptersDefaultExpanded) {
    currentActiveIndex.value = props.chapters.map((chapter, index) => index);
    return;
  }

  if (props.activeIndex !== null) {
    currentActiveIndex.value = [props.activeIndex];
    return;
  }
});

watch(() => props.activeIndex, newValue => {
  if (newValue !== null) {
    currentActiveIndex.value = [...currentActiveIndex.value, newValue];
  }
});

const getChapterActivitiesTotalDuration = (chapter: Chapter) => {
  const totalDurationInSeconds = chapter.contents
    .filter(activity => activity.type === CourseContentActivityType.Lesson && activity?.duration)
    .reduce((total, activity) => total + (activity?.duration ?? 0), 0);

  const hours = Math.floor(totalDurationInSeconds / 3600);
  const minutes = Math.floor((totalDurationInSeconds % 3600) / 60);
  const seconds = totalDurationInSeconds % 60;

  const formatWithLeadingZero = (num: number) => (num < 10 ? `0${num}` : num);

  if (hours === 0 && minutes === 0) {
    return '';
  }

  return hours ? `${hours}:${formatWithLeadingZero(minutes)}:${formatWithLeadingZero(seconds)}` : `${minutes}:${formatWithLeadingZero(seconds)}`;
};
</script>

<template>
  <Accordion
    v-if="course"
    :active-index="currentActiveIndex"
    lazy
    :multiple="true"
  >
    <AccordionTab
      v-for="(chapter, index) in chapters"
      :key="chapter.id"
      :pt="{
        root: {
          class: 'mt-4 mb-0 !shadow-medmastery'
        },
        headerAction: {
          class: 'p-4 border-none'
        },
        content: {
          class: 'p-0 border-none'
        }
      }"
    >
      <template #headericon="scope">
        <div class="flex flex-col w-full">
          <div class="flex flex-nowrap items-center w-full">
            <FontAwesomeIcon
              :icon="!!scope.active ? 'fa-caret-up' : 'fa-caret-down'"
              class="text-neutral-50 mr-2"
              :class="{'-mt-1': !scope.active}"
            />

            <div class="text-neutral-60 uppercase text-12 tracking-widest text-nowrap">
              <template v-if="chapter?.type === CourseChapterType.Kickoff">
                Kick-off quiz
              </template>

              <template v-else-if="chapter?.type === CourseChapterType.Mastery">
                Mastery quiz
              </template>

              <template v-else-if="chapter?.type === CourseChapterType.Summary">
                Summary
              </template>

              <template v-else>
                {{course?.type === CourseType.Course ? 'Chapter' : 'Case'}} {{index + 1}}
              </template>
            </div>

            <div
              v-if="chapter?.type !== CourseChapterType.Summary"
              class="w-full flex items-center justify-end gap-2"
            >
              <Tag
                v-if="showFreeChapterBadge(chapter)"
                severity="success"
                value="free chapter"
                class="uppercase text-nowrap text-neutral-90 bg-green-20"
                :pt="{
                  value: {
                    class: '!text-nowrap'
                  }
                }"
              />

              <div
                v-if="chapter?.locked"
                v-tippy="kickoffMasteryCourseChapterLockedTooltip(chapter)"
                class="w-6 h-6 bg-neutral-20 text-neutral-90 text-14 flex items-center justify-center rounded-full"
              >
                <FontAwesomeIcon
                  class="w-3"
                  icon="fa-lock"
                />
              </div>

              <div
                v-else-if="chapterProgress(chapter)?.isComplete"
                class="w-6 h-6 bg-green-60 text-white text-14 flex items-center justify-center rounded-full"
              >
                <FontAwesomeIcon
                  class="w-3"
                  icon="fa-check"
                />
              </div>

              <CircularProgressBar
                v-else-if="chapterProgress(chapter)?.completedActivities?.length > 0"
                :value="chapterProgress(chapter).completedActivities.length * 100 / chapterProgress(chapter).totalActivities"
              />

              <FontAwesomeIcon
                v-else
                icon="fa-circle-play"
                class="!w-6 !h-6 transition-all"
                :class="!!scope.active ? 'text-neutral-100' : 'text-neutral-40'"
              />
            </div>
          </div>

          <Divider
            v-if="chapter?.type !== CourseChapterType.Summary"
            class="my-4 -mx-4 w-[calc(100%+theme(space.8))]"
          />

          <div v-if="chapter?.type !== CourseChapterType.Summary">
            <div
              class="text-16 font-bold text-neutral-90"
              :class="[
                {'opacity-60': course?.kickoffAndMasteryQuizzesEnabled && chapter?.locked},
                {'mb-3': showChapterContentsAndTotalDuration(chapter)}
              ]"
            >
              <template v-if="chapter?.type === CourseChapterType.Kickoff">
                Before you start...
              </template>

              <template v-else-if="chapter?.type === CourseChapterType.Mastery">
                Almost done!
              </template>

              <template v-else>
                {{chapter.title}}
              </template>
            </div>

            <div
              class="chapter-description text-14 text-neutral-80 font-normal ellipsis line-clamp-4"
              :class="{'opacity-60': course?.kickoffAndMasteryQuizzesEnabled && chapter?.locked}"
              :style="!!scope.active || [CourseChapterType.Kickoff, CourseChapterType.Mastery].includes(chapter?.type as CourseChapterType) ? 'max-height: 65px' : ''"
            >
              <template v-if="chapter?.type === CourseChapterType.Kickoff">
                Let’s establish your current knowledge on the topic.
              </template>
              <template v-else-if="chapter?.type === CourseChapterType.Mastery">
                You’re almost done! It’s time to assess your knowledge again. Will you score better than the kick-off quiz?
              </template>
              <template v-else>
                <!-- eslint-disable vue/no-v-html -->
                <div v-html="chapter.description" />
              </template>
            </div>

            <div
              v-if="showChapterContentsAndTotalDuration(chapter)"
              class="flex flex-nowrap items-center justify-between text-neutral-80 text-14 font-bold transition-[0.2s]"
              :class="[
                {'mt-3': scope.active},
                [course?.kickoffAndMasteryQuizzesEnabled && chapter?.locked ? 'opacity-60' : '']
              ]"
            >
              <div class="flex flex-nowrap items-center">
                <FontAwesomeIcon
                  :icon="['far', 'book-bookmark']"
                  class="text-24 mr-2"
                />

                <div class="text-nowrap">
                  {{chapterContents(chapter)}}
                </div>
              </div>

              <div class="text-nowrap">
                {{getChapterActivitiesTotalDuration(chapter)}}
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #default>
        <div class="flex flex-col">
          <Divider class="-mx-4 my-0 w-[calc(100%+theme(space.8))]" />

          <div
            v-for="activity in chapter.contents"
            :key="activity.id"
            class="border-solid border-0 border-b border-neutral-20 last:border-b-0"
            :class="['activity', `activity-${activity.type}`]"
          >
            <CoursePlaylistActivity
              ref="activities"
              :activity="activity"
              :chapter="chapter"
              :is-workshop="course?.type === CourseType.Workshop"
              :is-kickoff-and-mastery-quiz-course="course.kickoffAndMasteryQuizzesEnabled"
            />
          </div>
        </div>
      </template>
    </AccordionTab>
  </Accordion>
</template>

<style lang="scss" scoped>
.chapter-description {
  overflow: hidden; transition: max-height 0.2s ease-in-out; max-height: 0;
}
</style>